import axiosInstance from "../../lib/axiosInstance";
import { toast } from "react-toastify";
import { AppDispatch } from "../store";
import { teacherSubjectActions } from "../slices/teacherSubject";
import { getErrorMessage } from "../../utils/errorUtils";
import { TeacherSubjectRequestDto } from "../../shared/dtos/teacherSubject/TeacherSubjectRequestDto";
import { TeacherSubjectResponseDto } from "../../shared/dtos/teacherSubject/TeacherSubjectResponseDto";
import { ApiResponseDto } from "../../shared/dtos/common/ApiResponseDto";
import { ApiResponseStatus } from "../../shared/enums/ApiResponseStatus";

export const createTeacherSubject =
  (values: TeacherSubjectRequestDto) => async (dispatch: AppDispatch) => {
    dispatch(teacherSubjectActions.setLoadingCreate());
    try {
      const { data } = await axiosInstance.post<
        ApiResponseDto<TeacherSubjectResponseDto>
      >("/teacher-subjects", values, { headers: { requiresAuth: true } });

      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        toast.success(data.message || "Teacher subject created successfully!");
        dispatch(teacherSubjectActions.createSuccess(data.data));
        dispatch(
          teacherSubjectActions.setServerStatus({
            operation: "create",
            status: data.statusCode || 201,
          }),
        );
      }
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(teacherSubjectActions.setErrorCreate(message));
    }
  };

export const getTeacherSubjects =
  (params?: { branchId?: string }) => async (dispatch: AppDispatch) => {
    dispatch(teacherSubjectActions.setLoadingFetchAll());
    try {
      const { data } = await axiosInstance.get<
        ApiResponseDto<TeacherSubjectResponseDto[]>
      >("/teacher-subjects", {
        headers: { requiresAuth: true },
        params,
      });

      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        dispatch(teacherSubjectActions.setItems(data.data));
      }
    } catch (error) {
      dispatch(teacherSubjectActions.setErrorFetchAll(getErrorMessage(error)));
    }
  };

export const getTeacherSubjectById =
  (id: string) => async (dispatch: AppDispatch) => {
    dispatch(teacherSubjectActions.setLoadingFetchOne());
    try {
      const { data } = await axiosInstance.get<
        ApiResponseDto<TeacherSubjectResponseDto>
      >(`/teacher-subjects/${id}`, { headers: { requiresAuth: true } });

      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        dispatch(teacherSubjectActions.setItem(data.data));
      }
    } catch (error) {
      dispatch(teacherSubjectActions.setErrorFetchOne(getErrorMessage(error)));
    }
  };

export const updateTeacherSubject =
  (payload: { id: string; values: TeacherSubjectRequestDto }) =>
  async (dispatch: AppDispatch) => {
    dispatch(teacherSubjectActions.setLoadingUpdate());
    try {
      const { data } = await axiosInstance.put<
        ApiResponseDto<TeacherSubjectResponseDto>
      >(`/teacher-subjects/${payload.id}`, payload.values, {
        headers: { requiresAuth: true },
      });

      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        toast.success(data.message || "Teacher subject updated successfully!");
        dispatch(teacherSubjectActions.updateSuccess(data.data));
        dispatch(
          teacherSubjectActions.setServerStatus({
            operation: "update",
            status: data.statusCode || 200,
          }),
        );
      }
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(teacherSubjectActions.setErrorUpdate(message));
    }
  };

export const deleteTeacherSubject =
  (id: string) => async (dispatch: AppDispatch) => {
    dispatch(teacherSubjectActions.setLoadingDelete());
    try {
      const { data } = await axiosInstance.delete<ApiResponseDto<void>>(
        `/teacher-subjects/${id}`,
        { headers: { requiresAuth: true } },
      );

      if (data.status === ApiResponseStatus.SUCCESS) {
        toast.success(data.message || "Teacher subject deleted successfully!");
        dispatch(teacherSubjectActions.deleteSuccess(id));
        dispatch(
          teacherSubjectActions.setServerStatus({
            operation: "delete",
            status: data.statusCode || 200,
          }),
        );
      }
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(teacherSubjectActions.setErrorDelete(message));
    }
  };

export const resetTeacherSubjectServerStatus =
  () => (dispatch: AppDispatch) => {
    dispatch(teacherSubjectActions.setServerStatus(null));
  };
